"use client";

import { useState, useEffect, useRef } from "react";
import { Form, Alert, Spinner, Button } from "react-bootstrap";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import { useNavigate } from "react-router-dom";

interface TwoFactorVerificationProps {
  userId: string;
  userName: string;
  onBack: () => void;
}

const RESEND_COOLDOWN_SECONDS = 120;

const TwoFactorVerification = ({ userId, userName, onBack }: TwoFactorVerificationProps) => {
  const [code, setCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();
  const { postData } = useDataFetch();

  const { mutateAsync: resendCode } = postData("/two-factor/send-code", {});
  const { mutateAsync: verify2FA } = postData("/users/login/verify-2fa", {});
        
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length !== 6) {
      setError("El código debe tener 6 dígitos");
      return;
    }

    try {
      setIsVerifying(true);
      setError(null);

      const tokenResponse = await verify2FA({ user_id: userId, code });

      if (tokenResponse?.data?.token) {
        localStorage.setItem("token", tokenResponse.data.token);
        localStorage.setItem("name", tokenResponse.data.name);
        localStorage.setItem("country", tokenResponse.data.country);
        localStorage.setItem("multyhealth_code", tokenResponse.data.multyhealth_code);
        
        navigate("/store", { replace: true });
      } else {
        setError("Código inválido o expirado. Por favor intenta nuevamente.");
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error al verificar el código");
    } finally {
      setIsVerifying(false);
    }
  };

  const startCooldown = (seconds = RESEND_COOLDOWN_SECONDS) => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setResendCooldown(seconds);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // El login ya envio el codigo: la espera corre desde que se monta la pantalla.
  useEffect(() => {
    startCooldown();
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResend = async () => {
    try {
      setIsResending(true);
      setError(null);

      await resendCode({ user_id: userId });

      startCooldown();
    } catch (err: any) {
      if (err?.message === "two_factor_code_resend_too_soon") {
        setError("Debes esperar 2 minutos antes de solicitar un nuevo código.");
      } else {
        setError("Error al reenviar el código");
      }
      startCooldown();
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
  };

  return (
    <div className="mw-480 ms-lg-auto">
      <div className="d-inline-block mb-4">
        <img
          src="/images/logo.svg"
          className="rounded-3 for-dark-logo"
          alt="login"
          width={200}
        />
      </div>

      <h3 className="fs-28 mb-2">Verificación de Doble Factor</h3>
      <p className="text-secondary mb-4">
        Hola {userName}, hemos enviado un código de 6 dígitos a tu número de WhatsApp.
        Por favor ingrésalo a continuación.
      </p>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      <Form onSubmit={handleVerify}>
        <Form.Group className="mb-4">
          <label className="label text-secondary">Código de Verificación</label>
          <Form.Control
            type="text"
            className="h-45 text-center fs-20 fw-bold"
            placeholder="000000"
            value={code}
            onChange={handleCodeChange}
            maxLength={6}
            autoFocus
          />
          <small className="text-muted">
            El código expira en 5 minutos
          </small>
        </Form.Group>

        <Form.Group className="mb-4">
          <button
            type="submit"
            className="btn btn-primary fw-medium py-2 px-3 w-100"
            disabled={isVerifying || code.length !== 6}
          >
            <div className="d-flex align-items-center justify-content-center py-1">
              {isVerifying ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                  <span className="ms-2">Verificando...</span>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined fs-20 text-white me-2">
                    verified_user
                  </span>
                  <span>Verificar Código</span>
                </>
              )}
            </div>
          </button>
        </Form.Group>

        <div className="d-flex justify-content-between align-items-center">
          <Button
            variant="link"
            className="text-secondary p-0"
            onClick={onBack}
            disabled={isVerifying}
          >
            ← Volver al inicio de sesión
          </Button>
          
          <Button
            variant="link"
            className="text-primary p-0"
            onClick={handleResend}
            disabled={isResending || resendCooldown > 0}
          >
            {isResending
              ? "Reenviando..."
              : resendCooldown > 0
              ? `Reenviar en ${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, "0")}`
              : "Reenviar código"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default TwoFactorVerification;
